import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BoutiqueFlashCards from '../components/BoutiqueFlashCards';
import BoutiqueSetupWizard from '../components/BoutiqueSetupWizard';
import './BoutiquePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BoutiquePage = () => {
  const [showFlashCards, setShowFlashCards] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [boutiques, setBoutiques] = useState([]);
  const [activeBoutique, setActiveBoutique] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const sessionToken = localStorage.getItem('sessionToken');
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionToken) {
      navigate('/login');
      return;
    }
    checkFirstTime();
    loadBoutiques();
  }, []);

  const checkFirstTime = async () => {
    try {
      const response = await axios.get(`${API}/boutiques/check/first-time`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      if (response.data.is_first_time) {
        setShowFlashCards(true);
      }
    } catch (err) {
      console.error('Error checking first-time:', err);
    }
  };

  const loadBoutiques = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/boutiques`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      if (response.data.success) {
        setBoutiques(response.data.boutiques);
        if (response.data.boutiques.length > 0 && !activeBoutique) {
          setActiveBoutique(response.data.boutiques[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading boutiques:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFlashCardsComplete = () => {
    setShowFlashCards(false);
    setShowWizard(true);
  };

  const handleWizardComplete = (boutiqueId) => {
    setShowWizard(false);
    loadBoutiques();
    setActiveBoutique(boutiqueId);
  };

  const handleWizardCancel = () => {
    setShowWizard(false);
    // If no boutiques exist, show flash cards again
    if (boutiques.length === 0) {
      setShowFlashCards(true);
    }
  };

  const handleCreateNew = () => {
    setShowWizard(true);
  };

  const handleDeleteBoutique = async (boutiqueId) => {
    if (!window.confirm('Are you sure you want to delete this boutique?')) {
      return;
    }

    try {
      await axios.delete(`${API}/boutiques/${boutiqueId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      loadBoutiques();
      if (activeBoutique === boutiqueId) {
        setActiveBoutique(null);
      }
    } catch (err) {
      alert('Failed to delete boutique');
    }
  };

  const getActiveBoutiqueData = () => {
    return boutiques.find(b => b.id === activeBoutique);
  };

  if (loading) {
    return (
      <div className="boutique-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your boutiques...</p>
        </div>
      </div>
    );
  }

  const activeBoutiqueData = getActiveBoutiqueData();

  return (
    <div className="boutique-page">
      {showFlashCards && <BoutiqueFlashCards onComplete={handleFlashCardsComplete} />}
      {showWizard && <BoutiqueSetupWizard onComplete={handleWizardComplete} onCancel={handleWizardCancel} />}

      <div className="boutique-header">
        <h1>My Boutique</h1>
        <button className="btn-create-boutique" onClick={handleCreateNew}>
          + Create New Boutique
        </button>
      </div>

      {boutiques.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎨</div>
          <h2>No Boutiques Yet</h2>
          <p>Create your first personalized boutique to get started!</p>
          <button className="btn-primary-large" onClick={handleCreateNew}>
            Create My First Boutique
          </button>
        </div>
      ) : (
        <>
          <div className="boutique-tabs">
            {boutiques.map(boutique => (
              <div
                key={boutique.id}
                className={`boutique-tab ${activeBoutique === boutique.id ? 'active' : ''}`}
                onClick={() => setActiveBoutique(boutique.id)}
              >
                <div className="tab-content">
                  <span className="tab-name">{boutique.name}</span>
                  <span className="tab-count">{boutique.total_generated || 0}</span>
                </div>
                <button
                  className="tab-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteBoutique(boutique.id);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {activeBoutiqueData && (
            <div className="boutique-content">
              <div className="boutique-info">
                <div className="info-card">
                  <div className="model-preview">
                    <img src={activeBoutiqueData.model_photo_url} alt="Model" />
                  </div>
                  <div className="info-details">
                    <h3>{activeBoutiqueData.name}</h3>
                    <div className="info-item">
                      <strong>Brands:</strong> {activeBoutiqueData.brands.length}
                    </div>
                    <div className="info-item">
                      <strong>Items per Brand:</strong> {activeBoutiqueData.items_per_brand}
                    </div>
                    <div className="info-item">
                      <strong>Frequency:</strong> {activeBoutiqueData.frequency}
                    </div>
                    <div className="info-item">
                      <strong>Credits per Cycle:</strong> {activeBoutiqueData.credits_per_cycle}
                    </div>
                    <div className="info-item">
                      <strong>Status:</strong>{' '}
                      <span className={activeBoutiqueData.is_active ? 'status-active' : 'status-inactive'}>
                        {activeBoutiqueData.is_active ? '✓ Active' : '✗ Paused'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="brands-list">
                  <h4>Selected Brands</h4>
                  <div className="brands-chips">
                    {activeBoutiqueData.brands.map((brand, idx) => (
                      <div key={idx} className="brand-chip-small">
                        {brand.name}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="try-ons-section">
                <div className="section-header">
                  <h2>Generated Looks</h2>
                  <div className="section-stats">
                    {activeBoutiqueData.total_generated || 0} total looks
                  </div>
                </div>

                <div className="try-ons-grid">
                  {activeBoutiqueData.total_generated === 0 ? (
                    <div className="no-try-ons">
                      <p>🎨 No try-ons generated yet.</p>
                      <p className="subtext">
                        {activeBoutiqueData.frequency === 'weekly' && 'Check back next Monday for your first batch!'}
                        {activeBoutiqueData.frequency === 'monthly' && 'Check back on the 1st of next month!'}
                        {activeBoutiqueData.frequency === 'on_upload' && 'We\'ll generate looks when brands add new items!'}
                      </p>
                    </div>
                  ) : (
                    <div className="coming-soon">
                      <p>📸 Try-on results will be displayed here</p>
                      <p className="subtext">Phase 2 will show your generated looks in a beautiful grid</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BoutiquePage;
